import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Calculator, Download, TrendingUp, DollarSign, Trash2, FileText, Pencil, UserPlus, X, Upload } from "lucide-react";

function formatApiError(error, fallback = "Erro inesperado") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d?.msg ? `${(d.loc || []).slice(-1).join(".")}: ${d.msg}` : JSON.stringify(d)))
      .join(" | ");
  }
  if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return error?.message || fallback;
}

const brl = (n) =>
  (Number(n) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parseNum = (v) => parseFloat((v ?? "").toString().replace(",", ".")) || 0;

// Distribuidoras com desconto CALCULADO automaticamente a partir da Tarifa Média
// discount% = ((tarifa_media - fio_b_ref) / tarifa_media) × 100
// Apenas estas distribuidoras estão habilitadas no Simulador Grupo A.
const DEFAULT_BASE_TARIFFS = {
  CELESC: 0.58,
  COPEL: 0.55,
  RGE: 0.63,
  CEMIG: 0.45,
  "ENERGISA-MT": 0.50,
};

// Normaliza o nome lido pela IA para a chave usada acima
const normalizeGroupADistributor = (raw) => {
  const up = (raw || "").trim().toUpperCase().replace(/\s+/g, "-");
  if (/ENERGISA.*(MT|MATO-GROSSO)(?!-DO)/.test(up)) return "ENERGISA-MT";
  return up;
};

const GROUP_A_ALLOWED_DISTRIBUTORS = Object.keys(DEFAULT_BASE_TARIFFS);

// Distribuidoras com tarifa única (TUSD+TE somadas) e sem dedução de ICMS
const SINGLE_TARIFF_DISTRIBUTORS = ["CEMIG"];

const isSingleTariffDistributor = (dist) =>
  SINGLE_TARIFF_DISTRIBUTORS.includes((dist || "").trim().toUpperCase());

const isAutoDiscountDistributor = (dist) => {
  const key = (dist || "").trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(DEFAULT_BASE_TARIFFS, key);
};

const computeAutoDiscount = (baseTariffs, dist, avgTariff) => {
  const key = (dist || "").trim().toUpperCase();
  const ref = baseTariffs[key];
  if (ref == null || !avgTariff || avgTariff <= 0) return 0;
  const pct = ((avgTariff - ref) / avgTariff) * 100;
  return pct < 0 ? 0 : pct;
};

// Campo numérico REUTILIZÁVEL — declarado FORA do componente pai para não
// ser recriado a cada render (o que fazia o input perder o foco a cada tecla).
const NumericField = ({
  label,
  name,
  value,
  onChange,
  step = "0.01",
  placeholder = "0,00",
  suffix,
  readOnly = false,
  hint,
}) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="relative">
      <Input
        type="number"
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={`grupo-a-field-${name}`}
        readOnly={readOnly}
        className={`${suffix ? "pr-12" : ""} ${readOnly ? "bg-slate-100 text-slate-700 cursor-not-allowed" : ""}`}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
    {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
  </div>
);

const EMPTY_FORM = {
  cooperative_name: "",
  installation_number: "",
  distributor: "",
  phone: "",
  discount_percentage: "",
  total_bill: "",
  demand_value: "",
  public_lighting: "",
  others: "",
  consumption_peak: "",
  consumption_off_peak: "",
  tusd_peak: "",
  tusd_off_peak: "",
  te_peak: "",
  te_off_peak: "",
  icms_tusd_peak: "",
  icms_tusd_off_peak: "",
  tariff_peak: "",
  tariff_off_peak: "",
};

export default function SimulatorGroupA({ user, onLogout }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [distributors, setDistributors] = useState([]);
  const [baseTariffs, setBaseTariffs] = useState(DEFAULT_BASE_TARIFFS);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiFilledFields, setAiFilledFields] = useState([]);
  const aiFileInputRef = useRef(null);
  const formRef = useRef(null);
  const resultRef = useRef(null);
  const navigate = useNavigate();

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [simResp, dsResp] = await Promise.all([
        axiosInstance.get("/simulations/group-a"),
        axiosInstance.get("/distributor-settings").catch(() => ({ data: [] })),
      ]);
      // Simulador Grupo A é restrito às 4 distribuidoras habilitadas.
      setDistributors(GROUP_A_ALLOWED_DISTRIBUTORS);
      // Tarifa Base Grupo A vem das Configurações (fallback: padrão do código)
      const merged = { ...DEFAULT_BASE_TARIFFS };
      (dsResp.data || []).forEach((d) => {
        const key = (d.distributor || "").toUpperCase();
        if (key in merged && Number(d.group_a_base_tariff) > 0) merged[key] = Number(d.group_a_base_tariff);
      });
      setBaseTariffs(merged);
      setSimulations(simResp.data || []);
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao carregar dados"));
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Ler fatura Grupo A por IA (Gemini 3 Flash via Emergent LLM Key)
  const handleAIExtract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Envie um PDF, JPG, PNG ou WebP.");
      e.target.value = "";
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Arquivo excede 15 MB.");
      e.target.value = "";
      return;
    }
    setAiExtracting(true);
    const toastId = toast.loading("Lendo fatura Grupo A com IA…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axiosInstance.post("/simulations/group-a/extract-invoice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const ex = resp?.data?.extracted || {};

      const patch = {};
      const filled = [];
      const setIf = (key, val) => {
        if (val === null || val === undefined || val === "") return;
        patch[key] = String(val);
        filled.push(key);
      };
      setIf("cooperative_name", ex.cooperative_name);
      setIf("installation_number", ex.installation_number);
      setIf("phone", ex.phone);
      setIf("consumption_peak", ex.consumption_peak);
      setIf("consumption_off_peak", ex.consumption_off_peak);
      setIf("tusd_peak", ex.tusd_peak);
      setIf("tusd_off_peak", ex.tusd_off_peak);
      setIf("te_peak", ex.te_peak);
      setIf("te_off_peak", ex.te_off_peak);
      setIf("icms_tusd_peak", ex.icms_tusd_peak);
      setIf("icms_tusd_off_peak", ex.icms_tusd_off_peak);
      setIf("tariff_peak", ex.tariff_peak);
      setIf("tariff_off_peak", ex.tariff_off_peak);
      setIf("demand_value", ex.demand_value);
      setIf("public_lighting", ex.public_lighting);
      setIf("others", ex.others);
      setIf("total_bill", ex.total_bill);

      // Distribuidora: só aceita se estiver na lista das 4 permitidas do Grupo A
      const distRaw = normalizeGroupADistributor(ex.distributor);
      if (GROUP_A_ALLOWED_DISTRIBUTORS.includes(distRaw)) {
        patch.distributor = distRaw;
        filled.push("distributor");
        // desconto será auto-calculado pelo useEffect a partir da Tarifa Média — nunca preencher pela IA
      } else if (distRaw) {
        toast.warning(
          `Distribuidora "${distRaw}" não é suportada no Grupo A (apenas CELESC, COPEL, RGE, CEMIG, ENERGISA-MT).`
        );
      }

      setFormData((prev) => ({ ...prev, ...patch }));
      setAiFilledFields(filled);
      toast.success(
        `IA preencheu ${filled.length} campo(s). Desconto será calculado automaticamente.`,
        { id: toastId }
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Erro ao ler fatura";
      toast.error(String(msg), { id: toastId });
    } finally {
      setAiExtracting(false);
      if (aiFileInputRef.current) aiFileInputRef.current.value = "";
    }
  };

  const autoDiscountActive = isAutoDiscountDistributor(formData.distributor);
  const singleTariff = isSingleTariffDistributor(formData.distributor);

  // Cálculo LIVE da Tarifa Média Compensável enquanto o usuário digita
  const livePreview = useMemo(() => {
    const cp = parseNum(formData.consumption_peak);
    const cfp = parseNum(formData.consumption_off_peak);
    let numerator;
    let hasTariff;
    if (singleTariff) {
      const trp = parseNum(formData.tariff_peak);
      const trfp = parseNum(formData.tariff_off_peak);
      numerator = trp * cp + trfp * cfp;
      hasTariff = trp > 0 || trfp > 0;
    } else {
      const tp = parseNum(formData.tusd_peak);
      const tfp = parseNum(formData.tusd_off_peak);
      const ep = parseNum(formData.te_peak);
      const efp = parseNum(formData.te_off_peak);
      const ip = parseNum(formData.icms_tusd_peak);
      const ifp = parseNum(formData.icms_tusd_off_peak);
      numerator = (tp + ep) * cp + (tfp + efp) * cfp - ip - ifp;
      hasTariff = tp > 0 || tfp > 0 || ep > 0 || efp > 0;
    }

    const totalKwh = cp + cfp;
    const compensable = numerator < 0 ? 0 : numerator;
    const avg = totalKwh > 0 ? compensable / totalKwh : 0;
    const hasAny = cp > 0 || cfp > 0 || hasTariff;

    return { compensable, avg, totalKwh, hasAny };
  }, [
    singleTariff,
    formData.consumption_peak,
    formData.consumption_off_peak,
    formData.tusd_peak,
    formData.tusd_off_peak,
    formData.te_peak,
    formData.te_off_peak,
    formData.icms_tusd_peak,
    formData.icms_tusd_off_peak,
    formData.tariff_peak,
    formData.tariff_off_peak,
  ]);

  // Auto-cálculo do Desconto a partir da Tarifa Média e da Tarifa Base (Configurações)
  useEffect(() => {
    if (!autoDiscountActive) return;
    const pct = computeAutoDiscount(baseTariffs, formData.distributor, livePreview.avg);
    const newVal = pct > 0 ? pct.toFixed(2) : "";
    setFormData((prev) =>
      prev.discount_percentage === newVal ? prev : { ...prev, discount_percentage: newVal }
    );
  }, [autoDiscountActive, baseTariffs, formData.distributor, livePreview.avg]);

  const formulaText = singleTariff
    ? "= (Tarifa_P × Cons_P + Tarifa_FP × Cons_FP) ÷ (Cons_P + Cons_FP)"
    : "= ((TUSD_P + TE_P) × Cons_P + (TUSD_FP + TE_FP) × Cons_FP − ICMS TUSD_P − ICMS TUSD_FP) ÷ (Cons_P + Cons_FP)";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cooperative_name || !formData.installation_number || !formData.distributor) {
      toast.error("Cooperado, Nº Instalação e Distribuidora são obrigatórios");
      return;
    }
    const cpV = parseNum(formData.consumption_peak);
    const cfpV = parseNum(formData.consumption_off_peak);
    const demV = parseNum(formData.demand_value);
    if (cpV <= 0 || cfpV <= 0 || demV <= 0) {
      toast.error("Fatura não é do Grupo A: é obrigatório ter Energia Ponta, Energia Fora Ponta e Demanda. Faturas do Grupo B devem usar o Simulador Grupo B.");
      return;
    }
    if (cpV + cfpV < 5000) {
      toast.error(`Consumo total de ${(cpV + cfpV).toLocaleString("pt-BR")} kWh é inferior ao mínimo de 5.000 kWh exigido para o Grupo A.`);
      return;
    }
    setCalculating(true);
    try {
      const payload = {
        cooperative_name: formData.cooperative_name.trim(),
        installation_number: formData.installation_number.trim(),
        distributor: formData.distributor,
        phone: (formData.phone || "").trim(),
        discount_percentage: parseNum(formData.discount_percentage),
        total_bill: parseNum(formData.total_bill),
        demand_value: parseNum(formData.demand_value),
        public_lighting: parseNum(formData.public_lighting),
        others: parseNum(formData.others),
        consumption_peak: parseNum(formData.consumption_peak),
        consumption_off_peak: parseNum(formData.consumption_off_peak),
        tusd_peak: parseNum(formData.tusd_peak),
        tusd_off_peak: parseNum(formData.tusd_off_peak),
        te_peak: parseNum(formData.te_peak),
        te_off_peak: parseNum(formData.te_off_peak),
        icms_tusd_peak: singleTariff ? 0 : parseNum(formData.icms_tusd_peak),
        icms_tusd_off_peak: singleTariff ? 0 : parseNum(formData.icms_tusd_off_peak),
        single_tariff: singleTariff,
        tariff_peak: singleTariff ? parseNum(formData.tariff_peak) : 0,
        tariff_off_peak: singleTariff ? parseNum(formData.tariff_off_peak) : 0,
      };
      const url = editingId
        ? `/simulations/group-a/${editingId}`
        : "/simulations/group-a";
      const method = editingId ? "put" : "post";
      const resp = await axiosInstance[method](url, payload);
      setResult(resp.data);
      setSimulations((prev) => {
        if (editingId) {
          return prev.map((s) => (s.id === editingId ? resp.data : s));
        }
        return [resp.data, ...prev];
      });
      toast.success(editingId ? "Simulação atualizada!" : "Simulação Grupo A calculada!");
      setEditingId(null);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao calcular simulação"));
    } finally {
      setCalculating(false);
    }
  };

  const handleEdit = (sim) => {
    setEditingId(sim.id);
    setResult(null);
    setFormData({
      cooperative_name: sim.cooperative_name || "",
      installation_number: sim.installation_number || "",
      distributor: sim.distributor || "",
      phone: sim.phone || "",
      discount_percentage: sim.discount_percentage != null ? String(sim.discount_percentage) : "",
      total_bill: sim.total_bill != null ? String(sim.total_bill) : "",
      demand_value: sim.demand_value != null ? String(sim.demand_value) : "",
      public_lighting: sim.public_lighting != null ? String(sim.public_lighting) : "",
      others: sim.others != null ? String(sim.others) : "",
      consumption_peak: sim.consumption_peak != null ? String(sim.consumption_peak) : "",
      consumption_off_peak: sim.consumption_off_peak != null ? String(sim.consumption_off_peak) : "",
      tusd_peak: sim.tusd_peak != null ? String(sim.tusd_peak) : "",
      tusd_off_peak: sim.tusd_off_peak != null ? String(sim.tusd_off_peak) : "",
      te_peak: sim.te_peak != null ? String(sim.te_peak) : "",
      te_off_peak: sim.te_off_peak != null ? String(sim.te_off_peak) : "",
      icms_tusd_peak: sim.icms_tusd_peak != null ? String(sim.icms_tusd_peak) : "",
      icms_tusd_off_peak: sim.icms_tusd_off_peak != null ? String(sim.icms_tusd_off_peak) : "",
      tariff_peak: sim.tariff_peak != null && sim.tariff_peak !== 0 ? String(sim.tariff_peak) : "",
      tariff_off_peak: sim.tariff_off_peak != null && sim.tariff_off_peak !== 0 ? String(sim.tariff_off_peak) : "",
    });
    toast.info(`Editando: ${sim.cooperative_name}`);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setResult(null);
    toast.info("Edição cancelada");
  };

  const handleAddCooperative = (sim) => {
    // Total kWh (compatível com o cadastro Grupo B, que usa 'consumption')
    const totalKwh =
      (Number(sim.consumption_peak) || 0) +
      (Number(sim.consumption_off_peak) || 0);
    const coopData = {
      name: sim.cooperative_name || "",
      installation_number: sim.installation_number || "",
      distributor: sim.distributor || "",
      phone: sim.phone || "",
      consumption: totalKwh ? String(totalKwh) : "",
      contracted_kwh: totalKwh ? String(totalKwh) : "",
      tariff_discount: sim.discount_percentage != null ? String(sim.discount_percentage) : "",
      current_tariff: sim.average_tariff != null ? String(sim.average_tariff) : "",
      email: "",
      cpf_cnpj: "",
      registration_type: "PF",
      consumer_group: "Grupo A",
      // Extra: dados de faturamento Grupo A (o cadastro pode ignorar campos desconhecidos)
      group_a_source_simulation_id: sim.id,
      group_a_total_bill: sim.total_bill,
      group_a_monthly_savings: sim.monthly_savings,
      group_a_annual_savings: sim.annual_savings,
    };
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(coopData)))); // UTF-8 safe (acentos/Ç)
    navigate(`/cooperatives/new?data=${encoded}`);
    toast.success("Redirecionando para cadastro de cooperado...");
  };

  const handleDownloadPdf = async (simId) => {
    try {
      const resp = await axiosInstance.get(`/simulations/group-a/${simId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `simulacao_grupo_a_${simId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao baixar PDF"));
    }
  };

  const handleDelete = async (simId) => {
    if (!window.confirm("Excluir esta simulação?")) return;
    try {
      await axiosInstance.delete(`/simulations/group-a/${simId}`);
      setSimulations((prev) => prev.filter((s) => s.id !== simId));
      toast.success("Simulação excluída");
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao excluir simulação"));
    }
  };

  // NumericField é declarado no escopo do módulo. Aqui apenas usamos-o
  // passando `value` e `onChange` explicitamente em cada uso.

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="simulator-grupo-a-page">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
            <Calculator className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Simulador Grupo A</h1>
            <p className="text-sm text-slate-500">
              Autoconsumo remoto para cooperados industriais/comerciais (com demanda contratada)
            </p>
            <p className="text-xs text-amber-700 mt-1" data-testid="group-a-requirements">
              Aceita apenas faturas com Energia Ponta, Fora Ponta e Demanda e consumo mínimo de 5.000 kWh. Faturas do Grupo B não são aceitas.
            </p>
          </div>
        </div>

        <Card data-testid="grupo-a-form-card" ref={formRef}>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between gap-2">
              <span>{editingId ? "Editando Simulação" : "Dados da Simulação"}</span>
              <div className="flex items-center gap-2">
                {editingId && (
                  <span className="text-xs font-medium px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded" data-testid="grupo-a-editing-badge">
                    Modo edição
                  </span>
                )}
                <input
                  ref={aiFileInputRef}
                  type="file"
                  accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  onChange={handleAIExtract}
                  data-testid="grupo-a-ai-file-input"
                />
                <Button
                  type="button"
                  onClick={() => aiFileInputRef.current?.click()}
                  disabled={aiExtracting}
                  data-testid="grupo-a-ai-extract-btn"
                  className="h-8 px-3 text-xs bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white"
                  title="Envie o PDF ou foto da fatura Grupo A para preencher automaticamente"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {aiExtracting ? "Lendo…" : "Ler fatura (IA)"}
                </Button>
              </div>
            </CardTitle>
            {aiFilledFields.length > 0 && (
              <div className="text-[11px] text-purple-700 mt-1" data-testid="grupo-a-ai-filled-summary">
                ✨ IA preencheu {aiFilledFields.length} campo(s) — revise antes de calcular.
              </div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Bloco 1: identificação */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600">Cooperado *</Label>
                  <Input
                    value={formData.cooperative_name}
                    onChange={(e) => handleFormChange("cooperative_name", e.target.value)}
                    data-testid="grupo-a-field-cooperative_name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Nº Instalação *</Label>
                  <Input
                    value={formData.installation_number}
                    onChange={(e) => handleFormChange("installation_number", e.target.value)}
                    data-testid="grupo-a-field-installation_number"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Telefone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleFormChange("phone", e.target.value)}
                    data-testid="grupo-a-field-phone"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Distribuidora *</Label>
                  <Select
                    value={formData.distributor}
                    onValueChange={(v) => handleFormChange("distributor", v)}
                  >
                    <SelectTrigger data-testid="grupo-a-field-distributor">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {distributors.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <NumericField
                  label="Desconto (%)"
                  name="discount_percentage"
                  step="0.1"
                  suffix="%"
                  value={formData.discount_percentage}
                  onChange={handleFormChange}
                  readOnly={autoDiscountActive}
                  hint={autoDiscountActive
                    ? `Calculado automaticamente para ${formData.distributor.toUpperCase()}: (Tarifa Média − ${baseTariffs[formData.distributor.trim().toUpperCase()]}) / Tarifa Média · base ajustável em Configurações`
                    : undefined}
                />
                <NumericField label="Valor da Fatura (R$)" name="total_bill" value={formData.total_bill} onChange={handleFormChange} />
              </div>

              {/* Bloco 2: encargos não compensáveis */}
              <div>
                <div className="text-sm font-semibold text-blue-700 mb-2 border-b border-cyan-500 pb-1">
                  Encargos Não Compensáveis
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <NumericField label="Valor da Demanda (R$)" name="demand_value" value={formData.demand_value} onChange={handleFormChange} />
                  <NumericField label="Iluminação Pública (R$)" name="public_lighting" value={formData.public_lighting} onChange={handleFormChange} />
                  <NumericField label="Outros (R$)" name="others" value={formData.others} onChange={handleFormChange} />
                </div>
              </div>

              {/* Bloco 3: consumo ponta / fora ponta */}
              <div>
                <div className="text-sm font-semibold text-blue-700 mb-2 border-b border-cyan-500 pb-1">
                  Consumo (kWh)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumericField label="Consumo Ponta" name="consumption_peak" suffix="kWh" value={formData.consumption_peak} onChange={handleFormChange} />
                  <NumericField label="Consumo Fora Ponta" name="consumption_off_peak" suffix="kWh" value={formData.consumption_off_peak} onChange={handleFormChange} />
                </div>
              </div>

              {singleTariff ? (
                /* Bloco 4 (CEMIG): tarifa única = TUSD + TE, sem ICMS */
                <div data-testid="grupo-a-single-tariff-block">
                  <div className="text-sm font-semibold text-blue-700 mb-2 border-b border-cyan-500 pb-1">
                    Tarifas (R$/kWh) — {formData.distributor.toUpperCase()}: Preço Unit. já inclui TUSD + TE
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <NumericField label="Tarifa Ponta" name="tariff_peak" step="0.0001" placeholder="0,0000" value={formData.tariff_peak} onChange={handleFormChange} hint="Preço Unit. (R$) da linha Energia Ativa HP" />
                    <NumericField label="Tarifa Fora Ponta" name="tariff_off_peak" step="0.0001" placeholder="0,0000" value={formData.tariff_off_peak} onChange={handleFormChange} hint="Preço Unit. (R$) da linha Energia Ativa HFP" />
                  </div>
                </div>
              ) : (
                <>
                  {/* Bloco 4: tarifas TUSD */}
                  <div>
                    <div className="text-sm font-semibold text-blue-700 mb-2 border-b border-cyan-500 pb-1">
                      Tarifas TUSD (R$/kWh)
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <NumericField label="TUSD Ponta" name="tusd_peak" step="0.0001" placeholder="0,0000" value={formData.tusd_peak} onChange={handleFormChange} />
                      <NumericField label="TUSD Fora Ponta" name="tusd_off_peak" step="0.0001" placeholder="0,0000" value={formData.tusd_off_peak} onChange={handleFormChange} />
                    </div>
                  </div>

                  {/* Bloco 5: tarifas TE */}
                  <div>
                    <div className="text-sm font-semibold text-blue-700 mb-2 border-b border-cyan-500 pb-1">
                      Tarifas TE (R$/kWh)
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <NumericField label="TE Ponta" name="te_peak" step="0.0001" placeholder="0,0000" value={formData.te_peak} onChange={handleFormChange} />
                      <NumericField label="TE Fora Ponta" name="te_off_peak" step="0.0001" placeholder="0,0000" value={formData.te_off_peak} onChange={handleFormChange} />
                    </div>
                  </div>

                  {/* Bloco 6: ICMS TUSD (deduzido do compensável) */}
                  <div data-testid="grupo-a-icms-block">
                    <div className="text-sm font-semibold text-blue-700 mb-2 border-b border-cyan-500 pb-1">
                      ICMS TUSD (deduzido do valor compensável)
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <NumericField label="ICMS TUSD Ponta (R$)" name="icms_tusd_peak" value={formData.icms_tusd_peak} onChange={handleFormChange} />
                      <NumericField label="ICMS TUSD Fora Ponta (R$)" name="icms_tusd_off_peak" value={formData.icms_tusd_off_peak} onChange={handleFormChange} />
                    </div>
                  </div>
                </>
              )}

              {/* Prévia LIVE: Tarifa Média Compensável */}
              {livePreview.hasAny && (
                <div
                  className="rounded-lg border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-yellow-50 p-4"
                  data-testid="grupo-a-live-avg-tariff"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase font-bold text-amber-800 tracking-wide">
                        Tarifa Média Compensável (prévia)
                      </div>
                      <div className="text-[11px] text-amber-800/80 mt-1">
                        {formulaText}
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className="text-3xl font-extrabold text-amber-900"
                        data-testid="grupo-a-live-avg-tariff-value"
                      >
                        R$ {Number(livePreview.avg || 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        <span className="text-sm font-medium text-amber-800 ml-1">/ kWh</span>
                      </div>
                      <div className="text-[11px] text-amber-800/80">
                        Compensável: R$ {brl(livePreview.compensable)} · Consumo total: {Number(livePreview.totalKwh || 0).toLocaleString("pt-BR")} kWh
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                {editingId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    data-testid="grupo-a-btn-cancel-edit"
                    className="border-amber-400 text-amber-800"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancelar edição
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setFormData(EMPTY_FORM); setResult(null); setEditingId(null); }}
                  data-testid="grupo-a-btn-reset"
                >
                  Limpar
                </Button>
                <Button
                  type="submit"
                  disabled={calculating}
                  data-testid="grupo-a-btn-submit"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {calculating
                    ? (editingId ? "Atualizando..." : "Calculando...")
                    : (editingId ? "Atualizar Simulação" : "Calcular Simulação")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* RESULT */}
        {result && (
          <Card ref={resultRef} data-testid="grupo-a-result-card" className="border-2 border-blue-500">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-white">
                <TrendingUp className="w-5 h-5" />
                Resultado da Simulação — {result.cooperative_name}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded border border-slate-200">
                  <div className="text-xs text-slate-500 uppercase font-semibold">Valor Compensável</div>
                  <div className="text-xl font-bold text-slate-800" data-testid="grupo-a-result-compensable">R$ {brl(result.compensable_value)}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded border border-slate-200">
                  <div className="text-xs text-slate-500 uppercase font-semibold">Fatura Enercooper</div>
                  <div className="text-xl font-bold text-slate-800" data-testid="grupo-a-result-enercooper">R$ {brl(result.enercooper_bill)}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded border border-slate-200">
                  <div className="text-xs text-slate-500 uppercase font-semibold">Nova Fatura Distribuidora</div>
                  <div className="text-xl font-bold text-slate-800" data-testid="grupo-a-result-newdist">R$ {brl(result.new_distributor_bill)}</div>
                </div>
              </div>
              <div className="p-4 bg-amber-50 rounded border border-amber-200">
                <div className="text-xs text-amber-700 uppercase font-semibold">Tarifa Média Compensável (R$/kWh)</div>
                <div className="text-2xl font-bold text-amber-900" data-testid="grupo-a-result-avg-tariff">
                  R$ {Number(result.average_tariff || 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                </div>
                <div className="text-[11px] text-amber-800 mt-1">
                  {result.single_tariff
                    ? "= (Tarifa_P × Cons_P + Tarifa_FP × Cons_FP) ÷ (Cons_P + Cons_FP)"
                    : "= ((TUSD_P + TE_P) × Cons_P + (TUSD_FP + TE_FP) × Cons_FP − ICMS TUSD_P − ICMS TUSD_FP) ÷ (Cons_P + Cons_FP)"}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-5 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-lg">
                  <div className="text-xs uppercase font-semibold opacity-90">Economia Mensal</div>
                  <div className="text-3xl font-bold">R$ {brl(result.monthly_savings)}</div>
                </div>
                <div className="p-5 bg-gradient-to-br from-cyan-600 to-cyan-700 text-white rounded-lg">
                  <div className="text-xs uppercase font-semibold opacity-90">Economia Anual</div>
                  <div className="text-3xl font-bold">R$ {brl(result.annual_savings)}</div>
                </div>
              </div>
              <div className="flex justify-end gap-2 flex-wrap">
                <Button
                  onClick={() => handleDownloadPdf(result.id)}
                  data-testid="grupo-a-btn-download-current"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar PDF
                </Button>
                <Button
                  onClick={() => handleAddCooperative(result)}
                  data-testid="grupo-a-btn-add-coop-current"
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Cadastrar Cooperado (Grupo A)
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* HISTORY */}
        <Card data-testid="grupo-a-history-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-blue-600" />
              Histórico de Simulações Grupo A
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-500">Carregando...</p>
            ) : simulations.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma simulação Grupo A registrada.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cooperado</TableHead>
                    <TableHead>Distribuidora</TableHead>
                    <TableHead className="text-right">Fatura Atual</TableHead>
                    <TableHead className="text-right">Economia Mensal</TableHead>
                    <TableHead className="text-right">Economia Anual</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {simulations.map((s) => (
                    <TableRow key={s.id} data-testid={`grupo-a-row-${s.id}`}>
                      <TableCell className="font-medium">{s.cooperative_name}</TableCell>
                      <TableCell>{s.distributor}</TableCell>
                      <TableCell className="text-right">R$ {brl(s.total_bill)}</TableCell>
                      <TableCell className="text-right text-blue-700 font-semibold">R$ {brl(s.monthly_savings)}</TableCell>
                      <TableCell className="text-right text-cyan-700 font-semibold">R$ {brl(s.annual_savings)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadPdf(s.id)}
                            data-testid={`grupo-a-btn-pdf-${s.id}`}
                            title="Baixar PDF"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(s)}
                            data-testid={`grupo-a-btn-edit-${s.id}`}
                            title="Editar simulação"
                            className="border-blue-300 hover:bg-blue-50"
                          >
                            <Pencil className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAddCooperative(s)}
                            data-testid={`grupo-a-btn-add-coop-${s.id}`}
                            title="Cadastrar como cooperado (Grupo A)"
                            className="border-emerald-300 hover:bg-emerald-50"
                          >
                            <UserPlus className="w-4 h-4 text-emerald-600" />
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(s.id)}
                              data-testid={`grupo-a-btn-delete-${s.id}`}
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
